import type { SpinnerProps } from './spinner.types'

import Box from '../box'
import { styled, useStyleConfig, vui } from '../core'
import T from '../t'
import { cs, filterUndefined } from '../utils'
import { spinnerPositionMapping, spinnerSpeedMapping } from './consts'

export const SpinnerCircle = styled.divBox`
  border-radius: round;
  display: flex;
  flex-shrink: 0;
`

/**
 * Displays a spinning circular element with optional text to signify loading content.
 *
 * Handles different positioning scenarios, speeds and coloring of the circle.
 */
export const Spinner = vui<'div', SpinnerProps>((props, ref) => {
  const {
    className,
    emptyColor,
    size,
    speed: speedProp = 'normal',
    text,
    textPosition = 'bottom',
    thickness,
    'aria-label': ariaLabel = typeof text === 'string' && text ? text : 'Loading',
    ...rest
  } = props

  const styles = useStyleConfig('Spinner', props)
  const isCustomSize = typeof size === 'number'
  const positionProps = spinnerPositionMapping[textPosition]
  const speed = spinnerSpeedMapping[speedProp] ?? speedProp

  const circleProps = filterUndefined({
    borderColor: emptyColor,
    borderWidth: isCustomSize ? (thickness ?? 4) : thickness,
    h: isCustomSize ? size : undefined,
    w: isCustomSize ? size : undefined,
  })

  return (
    <Box
      aria-atomic="true"
      aria-label={ariaLabel}
      center
      className={cs('vui-spinner', className)}
      gap={1}
      ref={ref}
      role="status"
      transitionDuration="fast"
      {...positionProps.container}
      {...styles.container}
      {...rest}
    >
      <SpinnerCircle
        animation={`vui-spin ${speed} linear infinite`}
        aria-hidden="true"
        className="vui-spinnerCircle"
        {...styles.circle}
        {...circleProps}
      />
      {!!text && (
        <T aria-hidden="true" className="vui-spinnerText" {...styles.text}>
          {text}
        </T>
      )}
    </Box>
  )
})

Spinner.displayName = 'Spinner'
export default Spinner
